import React, { useEffect, useState } from 'react';
import HeadlessModal from '../shared/HeadlessModal';
import { useTranslations } from 'next-intl';
import { RestaurantTag } from '@/lib/types/customer/RestaurantTags';
import { useRestaurantTagsDataStore } from '@/stores/cutomer/restaurantTags';
import { STOREFRONT } from '@/lib/utils/translation';
import useRestaurantTags from '@/hooks/useRestaurantTags';
import { childTagSwiperID } from '@/constant/configs';

interface ViewAllTagsModalProps {
  showViewAllTag: boolean;
  setShowViewAllTag: any;
  tagsData?: RestaurantTag[];
  vendorType?: string;
}

function ViewAllTagsModal({
  showViewAllTag,
  setShowViewAllTag,
  tagsData,
  vendorType,
}: Readonly<ViewAllTagsModalProps>) {
  const t = useTranslations('');

  const setSelectedSearchableTags = useRestaurantTagsDataStore(
    (state) => state.setSelectedSearchableTags
  );
  const tagsResData = useRestaurantTagsDataStore((state) => state.tagsData);
  const childrenTags = useRestaurantTagsDataStore(
    (state) => state.childrenTags
  );

  const [query, setQuery] = useState('');
  const [filteredTags, setFilteredTags] = useState<RestaurantTag[]>([]);

  const parentTag = tagsResData?.data?.find(
    (item) => item.id === tagsData?.[0]?.parent_id
  );

  const {
    searchWithChildTag,
    searchWithParentTag,
    getParamsTagIds,
    scrollToSlide,
  } = useRestaurantTags();

  useEffect(() => {
    if (tagsData && tagsData?.length > 0) {
      const copiedTagsArr = [...tagsData];
      setFilteredTags(
        copiedTagsArr?.toSorted((a, b) => {
          const nameA = a.name;
          const nameB = b.name;
          // Check if the name starts with a special character or a number
          const isSpecialA = /^[^a-zA-Z]/.test(nameA);
          const isSpecialB = /^[^a-zA-Z]/.test(nameB);
          // Prioritize special character or number names
          if (isSpecialA && !isSpecialB) return -1;
          if (!isSpecialA && isSpecialB) return 1;
          // Sort alphabetically within the categories
          return nameA.localeCompare(nameB);
        })
      );
    }
  }, [tagsData]);

  const handleTagInputSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchedText = event.target.value;
    if (searchedText?.length > 0) {
      setQuery(searchedText);
      const desiredTags = tagsData?.filter((tag) =>
        tag.name?.toLowerCase()?.includes(searchedText?.toLowerCase())
      );
      if (desiredTags) {
        setFilteredTags(desiredTags);
      }
    } else {
      setQuery('');
      if (tagsData) {
        setFilteredTags(tagsData);
      }
    }
  };

  return (
    <HeadlessModal
      isOpen={showViewAllTag}
      closeModal={() => {
        setShowViewAllTag(false);
      }}
      title={t(`${STOREFRONT.SEARCH}.all_categories`)}
      dialogPanelClassName="md:w-[70%] lg:w-[50%]"
    >
      <div className="mt-5">
        <div className="">
          <input
            type="search"
            placeholder={t(`${STOREFRONT.SEARCH}.search_category`)}
            className="searchPageSearch mb-[48px] mr-0 h-[48px] w-full rounded-xl border border-gray-100 bg-white px-7 py-[21px] shadow-sm outline-0 focus:border-gray-200 focus:ring-0 md:mb-0 md:mr-5"
            onChange={handleTagInputSearch}
          />
        </div>
        <div
          // className="mt-5 flex max-h-[470px] flex-wrap items-center gap-4 overflow-y-auto"
          className="mt-5 columns-1 gap-4 space-y-4 overflow-y-auto sm:columns-2 xl:columns-3"
        >
          {query.length <= 0 && (
            <span
              role="button"
              onClick={() => {
                searchWithParentTag(parentTag, true);
                setShowViewAllTag(false);
              }}
              className={`block w-full rounded border border-gray-50 px-6 py-2 ${
                parentTag && getParamsTagIds()?.includes(parentTag.id)
                  ? 'text-primary'
                  : 'text-paragraph'
              } shadow-sm`}
            >
              {t(`${STOREFRONT.SEARCH}.all`)}
            </span>
          )}
          {filteredTags?.map((tag: RestaurantTag, idx: number) => {
            return (
              <span
                key={`vat-${tag.id}-${idx}`}
                role="button"
                onClick={() => {
                  setSelectedSearchableTags(tag);
                  const findIdx =
                    childrenTags
                      ?.slice(0, 15)
                      ?.findIndex((item) => item.id === tag.id) ?? -1;
                  if (findIdx < 15 && findIdx > -1) {
                    scrollToSlide(childTagSwiperID, findIdx);
                  }
                  searchWithChildTag({ tag, vendorType });
                  setShowViewAllTag(false);
                }}
                className={`block w-full rounded border border-gray-50 px-6 py-2 ${
                  getParamsTagIds()?.includes(tag.id)
                    ? 'text-primary'
                    : 'text-paragraph'
                } shadow-sm`}
              >
                {tag.name}
              </span>
            );
          })}
        </div>
      </div>
    </HeadlessModal>
  );
}

export default ViewAllTagsModal;
